"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrderSummary = OrderSummary;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const prop_types_1 = __importDefault(require("prop-types"));
const classnames_1 = __importDefault(require("classnames"));
const camelCase_1 = __importDefault(require("lodash/camelCase"));
const pattern_library_components_button_1 = require("@saatva-bits/pattern-library.components.button");
const pattern_library_components_icon_1 = require("@saatva-bits/pattern-library.components.icon");
const pattern_library_utils_price_format_1 = require("@saatva-bits/pattern-library.utils.price-format");
const utils_1 = require("./utils");
const line_item_1 = __importDefault(require("./line-item"));
const accordion_1 = __importDefault(require("./accordion"));
const order_summary_module_scss_1 = __importDefault(require("./order-summary.module.scss"));
const payment_icons_1 = __importDefault(require("./payment-icons"));
const mrc_tax_1 = __importDefault(require("./mrc-tax"));
function OrderSummary({ className, dataSelector, cart, variant = 'cart', showWhiteGlove, showFedex, checkoutUrl }) {
    const isConfirmation = variant === 'confirmation';
    const classes = (0, classnames_1.default)(order_summary_module_scss_1.default.container, className, {
        [order_summary_module_scss_1.default.isConfirmation]: isConfirmation
    });
    const subTotal = (0, react_1.useMemo)(() => {
        if (cart) {
            return (0, pattern_library_utils_price_format_1.formatToCurrency)(cart.subtotal);
        }
        return '$0.00';
    }, [cart]);
    const sortedDiscounts = (0, react_1.useMemo)(() => (0, utils_1.sortDiscounts)(cart), [cart]);
    const renderDiscounts = (0, react_1.useMemo)(() => {
        let discounts;
        const totalDiscounts = Math.abs(cart && cart.discount || 0);
        if (cart && totalDiscounts > 0) {
            // Confirmation page will display one line with total discounts
            if (isConfirmation) {
                discounts = (0, jsx_runtime_1.jsx)(line_item_1.default, { label: "Discounts & Savings", value: `-${(0, pattern_library_utils_price_format_1.formatToCurrency)(totalDiscounts)}`, dataSelector: "orderSummary_discounts" });
            }
            else {
                const oneDiscount = sortedDiscounts.length === 1;
                // One discount will be display it without dropdown
                if (oneDiscount) {
                    const discount = sortedDiscounts[0];
                    discounts = (0, jsx_runtime_1.jsx)(line_item_1.default, { label: discount.label, value: `-${(0, pattern_library_utils_price_format_1.formatToCurrency)(discount.amount)}`, dataSelector: `orderSummary_discount_${(0, camelCase_1.default)(discount.label)}`, oneDiscount: true }, "oneDiscount");
                    // Multiple discounts will be display it with dropdown
                }
                else {
                    discounts = (0, jsx_runtime_1.jsx)(accordion_1.default, { label: "Discounts & Savings", amount: `-${(0, pattern_library_utils_price_format_1.formatToCurrency)(totalDiscounts)}`, dataSelector: "orderSummary_discounts", children: sortedDiscounts.map((discount, index) => ((0, jsx_runtime_1.jsx)(line_item_1.default, { label: discount.label, value: `-${(0, pattern_library_utils_price_format_1.formatToCurrency)(discount.amount)}`, dataSelector: `orderSummary_discount_${(0, camelCase_1.default)(discount.label)}` }, index))) });
                }
            }
        }
        return discounts;
    }, [cart, sortedDiscounts]);
    const isTaxKnown = (0, react_1.useMemo)(() => cart && cart.tax && cart.tax.amount > 0, [cart]);
    const renderTax = (0, react_1.useMemo)(() => {
        if (isConfirmation) {
            return null;
        }
        const taxLabel = variant === 'cart' ? 'Tax calculated in checkout' : 'Tax';
        let taxLineItem = (0, jsx_runtime_1.jsx)(line_item_1.default, { label: taxLabel, value: '$-', dataSelector: 'orderSummary_orderTax' });
        if (isTaxKnown) {
            taxLineItem = (0, jsx_runtime_1.jsx)(line_item_1.default, { label: "Tax", value: (0, pattern_library_utils_price_format_1.formatToCurrency)(cart.tax.amount), dataSelector: 'orderSummary_orderTax' });
        }
        return taxLineItem;
    }, [cart, isTaxKnown]);
    const renderButton = (0, react_1.useMemo)(() => {
        let checkoutButton;
        if (cart && variant === 'cart') {
            const hasItems = cart.totalItems > 0;
            const cartLabel = 'Proceed to Checkout';
            checkoutButton = (0, jsx_runtime_1.jsx)("div", { className: order_summary_module_scss_1.default.buttonPanel, children: (0, jsx_runtime_1.jsxs)(pattern_library_components_button_1.Button, { className: order_summary_module_scss_1.default.checkoutButton, kind: "primary", "data-selector": 'orderSummary_checkout', disabled: !hasItems, href: checkoutUrl, children: [cartLabel, hasItems && ((0, jsx_runtime_1.jsx)(pattern_library_components_icon_1.Icon, { className: (0, classnames_1.default)('u-marginHorizontal--sm', order_summary_module_scss_1.default.secureIcon), titleId: 'secure-card-number', name: "secure", description: "Secure", alt: "Secure transaction" }))] }) });
        }
        return checkoutButton;
    }, [cart]);
    const orderTotal = (0, react_1.useMemo)(() => {
        if (cart && isTaxKnown) {
            return (0, pattern_library_utils_price_format_1.formatToCurrency)(cart.total);
        }
        return (0, pattern_library_utils_price_format_1.formatToCurrency)(cart ? cart.total : 0);
    }, [cart, isTaxKnown]);
    const renderTaxLineItem = (0, react_1.useMemo)(() => {
        if (isConfirmation) {
            return (0, jsx_runtime_1.jsx)(line_item_1.default, { label: "Tax (Sales)", value: (0, pattern_library_utils_price_format_1.formatToCurrency)(cart.tax.amount), dataSelector: "orderSummary_orderTax" });
        }
        if (variant === 'cart') {
            return (0, jsx_runtime_1.jsx)(line_item_1.default, { label: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("b", { children: "Total" }), ' ', !isTaxKnown && ((0, jsx_runtime_1.jsx)("span", { children: "before tax" }))] }), value: (0, jsx_runtime_1.jsx)("b", { children: orderTotal }), isTotal: true, dataSelector: "orderSummary_total" });
        }
        return ((0, jsx_runtime_1.jsx)(line_item_1.default, { label: "Order total", value: orderTotal, datasewlector: "orderSummary_total", isCheckout: true }));
    }, [cart, isTaxKnown, orderTotal]);
    return ((0, jsx_runtime_1.jsxs)("div", { "data-selector": dataSelector, className: classes, children: [!isConfirmation && (0, jsx_runtime_1.jsx)("h2", { className: order_summary_module_scss_1.default.title, children: "Order summary" }), (0, jsx_runtime_1.jsx)(line_item_1.default, { label: "Order subtotal", value: subTotal, dataSelector: "orderSummary_subtotal" }), renderDiscounts, showWhiteGlove && (0, jsx_runtime_1.jsx)(line_item_1.default, { label: "White Glove Delivery", value: "Free", dataSelector: "orderSummary_whiteGloveDelivery" }), showFedex && (0, jsx_runtime_1.jsx)(line_item_1.default, { label: "Ground Delivery", value: "Free", dataSelector: "orderSummary_fedexGroundShipping" }), cart && !!cart.mrc && (0, jsx_runtime_1.jsx)(mrc_tax_1.default, { mrc: cart.mrc, variant: variant }), renderTax, renderTaxLineItem, renderButton, variant === 'cart' && ((0, jsx_runtime_1.jsx)(payment_icons_1.default, {})), isConfirmation && (0, jsx_runtime_1.jsx)(line_item_1.default, { label: "Total", value: orderTotal, dataSelector: "orderSummary_total", isCheckout: true })] }));
}
OrderSummary.propTypes = {
    /** Add a custom classname for additional styling of the OrderSummary or its text */
    className: prop_types_1.default.string,
    /** A unique id attached to the 'data-selector' attribute that allows for easy DOM querying by third party tools */
    dataSelector: prop_types_1.default.string,
    /** Current cart object */
    cart: prop_types_1.default.object.isRequired,
    /** Variant of the OrderSummary component `cart` | `checkout` | `confirmation` */
    variant: prop_types_1.default.oneOf(['cart', 'checkout', 'confirmation']),
    /* Show white glove line item */
    showWhiteGlove: prop_types_1.default.bool,
    /* Show fedex line item */
    showFedex: prop_types_1.default.bool,
    /* Checkout URL */
    checkoutUrl: prop_types_1.default.string,
};
//# sourceMappingURL=order-summary.js.map